import React, { useEffect, useState } from 'react';
import { Link, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';
import { getWithExpiry } from '../Domain/Helpers/LocalStorageHelper';
import { logoutCall } from '../Infrastructure/Services/AuthService';
import UISidebar from '../Domain/Components/UI/Sidebar/Sidebar';
import UINavbar from '../Domain/Components/UI/Navbar/Navbar';
import LoginPage from './Pages/Login/LoginPage';
import AggregatorsPage from './Pages/Aggregators/AggregatorsPage';
import CasinosPage from './Pages/Casinos/CasinosPage';
import GamesPage from './Pages/Games/GamesPage';
import ProvidersPage from './Pages/Providers/ProvidersPage';
import CasinoGamesPage from './Pages/CasinoGames/CasinoGamesPage';
import SettingsPage from './Pages/Settings/SettingsPage';

function App() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loggedIn, setLoggedIn] = useState(() => getWithExpiry('token') !== null);
  const [sideBarContents, setSideBarContents] = useState([]);

  const navigate = useNavigate();
  const location = useLocation();

  const logout = async () => {
    const token = getWithExpiry('token');
    try { await logoutCall(token); } catch { /* clear locally */ }
    setLoggedIn(false);
  };

  useEffect(() => {
    if (loggedIn) {
      const roles = getWithExpiry('roles') ?? [];
      if (!roles || roles.length === 0) { setLoggedIn(false); return; }

      // gis-admin can see the import tabs
      const isGisAdmin = roles.includes('gis-admin');

      setSideBarContents([
        {
          submenuTitle: 'Aggregators',
          submenuIcon: '',
          contents: [
            { linkEl: <Link to="/aggregators">Aggregators</Link>, path: '/aggregators' },
          ],
        },
        {
          submenuTitle: 'Casinos',
          submenuIcon: '',
          contents: [
            { linkEl: <Link to="/casinos">Casinos</Link>, path: '/casinos' },
          ],
        },
        {
          submenuTitle: 'Providers',
          submenuIcon: '',
          contents: [
            { linkEl: <Link to="/providers">Providers</Link>, path: '/providers' },
          ],
        },
        {
          submenuTitle: 'Import Games',
          submenuIcon: '',
          hidden: !isGisAdmin,
          contents: [
            { linkEl: <Link to="/games">Import Games</Link>, path: '/games' },
          ],
        },
        {
          submenuTitle: 'Casino Games',
          submenuIcon: '',
          contents: [
            { linkEl: <Link to="/casino-games">Casino Games</Link>, path: '/casino-games' },
          ],
        },
        {
          submenuTitle: 'Settings',
          submenuIcon: '',
          hidden: !isGisAdmin,
          contents: [
            { linkEl: <Link to="/settings">Settings</Link>, path: '/settings' },
          ],
        },
        {
          submenuTitle: 'Account',
          submenuIcon: '',
          contents: [
            { linkEl: <button onClick={logout}>Logout</button>, path: null },
          ],
        },
      ]);

      // Redirect away from login
      if (location.pathname.includes('login')) {
        const params = new URLSearchParams(location.search);
        navigate(params.get('returnUrl') ?? '/aggregators');
      }
    } else {
      setSideBarContents([
        {
          submenuTitle: 'Casino Games',
          submenuIcon: '🎰',
          contents: [
            { linkEl: <Link to="/casino-games">Casino Games</Link>, path: '/casino-games' },
          ],
        },
        {
          submenuTitle: 'Account',
          submenuIcon: '👤',
          contents: [
            { linkEl: <Link to="/login">Login</Link>, path: '/login' },
          ],
        },
      ]);
      localStorage.removeItem('roles');
      if (!location.pathname.includes('login')) {
        navigate(`/login?returnUrl=${location.pathname}${location.search}`);
      }
    }
  }, [loggedIn]);

  return (
    <div id="wrapper" className={sidebarOpen ? 'toggled' : ''}>
      <ToastContainer position="bottom-right" autoClose={5000} theme="dark" />
      <div className="overlay" />

      <UISidebar contents={sideBarContents} logoSrc="/logo192.png" />

      <div id="page-content-wrapper">
        <UINavbar toggleSidebar={() => setSidebarOpen(o => !o)} />

        <div className="container-fluid app-container px-4 pt-3">
          <Routes>
            <Route path="/login" element={<LoginPage setLoggedIn={setLoggedIn} loggedIn={loggedIn} />} />
            <Route path="/aggregators" element={<AggregatorsPage setLoggedIn={setLoggedIn} />} />
            <Route path="/casinos"     element={<CasinosPage     setLoggedIn={setLoggedIn} />} />
            <Route path="/games"       element={<GamesPage       setLoggedIn={setLoggedIn} />} />
            <Route path="/providers"    element={<ProvidersPage    setLoggedIn={setLoggedIn} />} />
            <Route path="/casino-games"  element={<CasinoGamesPage  setLoggedIn={setLoggedIn} />} />
            <Route path="/settings"      element={<SettingsPage      setLoggedIn={setLoggedIn} />} />
            <Route path="/" element={<Navigate to="/aggregators" replace />} />
            <Route path="*" element={<Navigate to="/aggregators" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default App;
